import { utcToZonedTime } from 'date-fns-tz';
import Highcharts, {
  Options as HighchartsOptions,
  SeriesOptions,
  XAxisOptions,
  YAxisOptions,
  SeriesLineOptions,
} from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsBoost from 'highcharts/modules/boost';
import { isEmpty } from 'ramda';
import React, { memo, useRef } from 'react';
import ReactResizeDetector from 'react-resize-detector';

import { useAuth } from '../../../app/AuthorizationProvider';
import { getZonedDateTimezoneOffset } from '../../../utils/date';
import { ACTIVE_PLOT_BAND_COLOR, ComposedChartEntry, staticChartOptions } from '../config';
import {
  chartTooltipFormatter,
  chartXAxisLabelFormatter,
  chartLegendLabelFormatter,
  generateChartGapSeriesOptions,
  getChartSeriesColor,
  chartYAxisLabelFormatter,
} from '../utils';
import { AdjustmentRangeType, SeriesConfig } from './Chart';

HighchartsBoost(Highcharts);

export interface ComposedChartDataEntry extends ComposedChartEntry, SeriesConfig {
  baseSeriesId: string;
  id: string;
  nameKey: string;
}

/* eslint-disable react/no-unused-prop-types */
export interface ComposedChartProps {
  dataTestID: string;
  dateFormat: {
    full: string;
    short: string;
  };
  endDateTime: number;
  highlightedRange: AdjustmentRangeType;
  isSidebarOpen: boolean;
  labels: {
    xAxis: string;
    yAxis: string;
  };
  seriesOptions: ComposedChartDataEntry[];
  timezone: string;
  valuePrecision?: number;
  localeObj?: Locale;
}
/* eslint-enables react/no-unused-prop-types */

const generateChartConfig: (props: ComposedChartProps, locale: string) => HighchartsOptions = (
  {
    dateFormat,
    endDateTime,
    highlightedRange,
    isSidebarOpen,
    labels,
    seriesOptions,
    timezone,
    valuePrecision,
    localeObj,
  },
  locale
) => ({
  ...staticChartOptions,
  chart: {
    ...staticChartOptions.chart,
    height: isSidebarOpen ? 319 : 396,
    spacing: [34, 40, 7, 13],
  },
  legend: {
    margin: 0,
    itemDistance: 28,
    x: 35,
    labelFormatter: chartLegendLabelFormatter,
    symbolWidth: 0,
    shadow: false,
    useHTML: true,
  },
  series: [
    ...seriesOptions.map(
      ({ adjustmentId, baseSeriesId, color, data, id, inCollision, inPreview, name, nameKey, useGapConfig }) =>
        useGapConfig
          ? generateChartGapSeriesOptions(data, baseSeriesId, id, color)
          : {
              ...(staticChartOptions.series as SeriesOptions[])[0],
              color,
              data,
              id,
              name,
              nameKey,
              fillColor: getChartSeriesColor(inCollision, inPreview),
              linkedTo: adjustmentId ? baseSeriesId : undefined,
              showInLegend: !adjustmentId,
              step: 'left' as SeriesLineOptions['step'],
              type: (adjustmentId ? 'area' : 'line') as SeriesLineOptions['type'],
            }
    ),
  ] as SeriesLineOptions[],
  time: {
    getTimezoneOffset: (timestamp: number) => getZonedDateTimezoneOffset(utcToZonedTime(timestamp, timezone), timezone),
    useUTC: true,
  },
  tooltip: {
    ...staticChartOptions.tooltip,
    formatter: chartTooltipFormatter(labels.xAxis, dateFormat.full, endDateTime, timezone, locale, valuePrecision),
  },
  xAxis: {
    ...staticChartOptions.xAxis,
    labels: {
      ...(staticChartOptions.xAxis as XAxisOptions).labels,
      formatter: chartXAxisLabelFormatter(dateFormat.short, timezone, localeObj),
    },
    plotBands: isEmpty(highlightedRange)
      ? []
      : [
          {
            color: ACTIVE_PLOT_BAND_COLOR,
            from: highlightedRange.startDateTime,
            to: highlightedRange.endDateTime,
          },
        ],
    title: {
      ...(staticChartOptions.xAxis as XAxisOptions).title,
      text: labels.xAxis,
    },
  },
  yAxis: {
    ...staticChartOptions.yAxis,
    labels: {
      ...(staticChartOptions.yAxis as YAxisOptions).labels,
      formatter: chartYAxisLabelFormatter(locale),
    },

    title: {
      ...(staticChartOptions.yAxis as YAxisOptions).title,
      text: labels.yAxis,
    },
  },
});

export const ComposedChartComponent: React.FC<ComposedChartProps> = (props) => {
  const { locale } = useAuth();

  const chartRef = useRef<HighchartsReact>(null);
  const { dataTestID } = props;

  const onResizeHandler = () => {
    if (chartRef && chartRef.current) {
      chartRef.current.chart.reflow();
    }
  };

  return (
    <ReactResizeDetector handleWidth onResize={onResizeHandler}>
      <div className="mb-5" data-test-id={dataTestID}>
        <HighchartsReact
          highcharts={Highcharts}
          options={generateChartConfig(props, locale)}
          ref={chartRef}
          updateArgs={[true, true, false]}
        />
      </div>
    </ReactResizeDetector>
  );
};

export const ComposedChart = memo(ComposedChartComponent);
